/**
 * @module useCloudRoutingConfigurationPageQuery
 *
 * This module handles the GraphQL queries related to the configuration of routing within the cloud.
 * It provides the `useCloudRoutingConfigurationPageQuery` hook to interact with cloud routing configurations
 * for a specified service and graph variant.
 */
import { gql, useQuery } from '@apollo/client';

import { RouterConfigEditorFragment } from 'src/app/graph/variantSettingsPage/variantSettingsCards/routerConfigEditor/RouterConfigEditor';
import {
  CloudRoutingConfigurationPageQuery,
  CloudRoutingConfigurationPageQueryVariables,
} from 'src/lib/graphqlTypes/types';

import { CloudOnboardingFragment } from './useCloudOnboarding';

/**
 * GraphQL fragment to fetch routing configuration for a specific service ID and graph variant.
 * @type {DocumentNode}
 */
export const CloudRoutingConfigurationFragment = gql`
  fragment CloudRoutingConfigurationFragment on Service {
    id
    graphType
    variant(name: $graphVariant) {
      id
      url
      name
      router {
        id
        status
        routerVersion {
          core
          configSchema
        }
      }
      latestLaunch {
        id
        createdAt
        status
      }
      permissions {
        canManageBuildConfig
        canQueryBuildConfig
      }
      ...RouterConfigEditorFragment
    }
  }
  ${RouterConfigEditorFragment}
`;

/**
 * GraphQL query to fetch cloud routing configuration details using various input parameters.
 * @type {DocumentNode}
 */
const CLOUD_CONFIGURATION_PAGE_QUERY = gql`
  query CloudRoutingConfigurationPageQuery(
    $serviceId: ID!
    $graphVariant: String!
    $accountId: ID!
    $graphRef: String!
  ) {
    service(id: $serviceId) {
      id
      ...CloudRoutingConfigurationFragment
    }
    account(id: $accountId) {
      id
      ...CloudOnboardingFragment
    }
  }
  ${CloudRoutingConfigurationFragment}
  ${CloudOnboardingFragment}
`;

/**
 * Hook to execute a query for cloud routing configurations.
 *
 * @param {Object} params Parameters to use with the query.
 * @param {string} params.serviceId The ID of the service.
 * @param {string} params.graphVariant The graph variant.
 * @param {string} params.accountId The ID of the account associated with the service.
 * @param {string} params.graphRef The combined graph variant and graph name.
 * @param {boolean} params.skip A flag indicating whether to skip executing the query.
 * @returns {Object} The result of the query.
 */
export const useCloudRoutingConfigurationPageQuery = ({
  serviceId,
  graphVariant,
  accountId,
  graphRef,
  skip,
}: {
  serviceId: string;
  graphVariant: string;
  accountId: string;
  graphRef: string;
  skip: boolean;
}) => {
  return useQuery<
    CloudRoutingConfigurationPageQuery,
    CloudRoutingConfigurationPageQueryVariables
  >(CLOUD_CONFIGURATION_PAGE_QUERY, {
    variables: {
      serviceId,
      graphVariant,
      accountId,
      graphRef,
    },
    skip,
  });
};
